import { Button, Icon } from 'components/common';
import CoinProgressBar from 'components/promotion/CoinProgressBar';
import CoinPurchaseAnimation, { CoinPurchaseAnimationRef } from 'components/promotion/CoinPurchaseAnimation';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const CoinSinglePromotion = () => {
  const { close, closeWithAnimation, sheets, updateOptions, updateData } = useBottomSheet();
  const sheet = sheets.CoinSinglePromotion;
  const { productId, bonusRatio = 50, imageLoaded, yearEnd } = sheet.data;
  const [details, setDetails] = useState<{ coinAmount: number; price: string } | null>(null);
  const [progressCompleted, setProgressCompleted] = useState(false);

  const coinPurchaseAnimationRef = useRef<CoinPurchaseAnimationRef>(null);

  const imgUrl = useMemo(() => {
    if (yearEnd) {
      return `/imgweb/promo/year_end.png`;
    } else {
      return `/images/shop/coins/new_${productId.replace('ccoin', 'coin')}.png`;
    }
  }, [productId, yearEnd]);

  const preloadImage = useCallback(() => {
    const img = new Image();
    img.src = imgUrl;
    img.onload = () => {
      setProgressCompleted(true);
      setTimeout(() => {
        updateData(sheet.id, { imageLoaded: true });
      }, 700);
      updateOptions(sheet.id, {
        containerStyle: { animation: 'none' },
      });
    };
  }, [sheet.id, updateOptions, updateData, imgUrl]);

  useEffect(() => {
    const details = PageShop.getProductDetails(productId);
    setDetails(details);

    preloadImage();

    return () => {
      PurchaseManager.detach();
      PageShop.resetPromo();
    };
  }, [productId, preloadImage]);

  useEffect(() => {
    Braze.applyPageShopProtocol(new URL(`thebermuda://deeplink/promo?bonus_ratio=${bonusRatio}`));
    PurchaseManager.attach(function (err: any, coin: any) {
      if (err) return;

      coinPurchaseAnimationRef.current?.show().then(() => {
        closeWithAnimation(sheet.id);
      });
    });
  }, [bonusRatio, closeWithAnimation, sheet.id, coinPurchaseAnimationRef]);

  useEffect(() => {
    if (imageLoaded) return;

    updateOptions(sheet.id, {
      containerStyle: {
        animation: 'highlight 2s infinite',
        animationFillMode: 'forwards',
        backgroundSize: '800% 100%',
        backgroundImage: 'linear-gradient(-75deg, transparent 0%, transparent 12.5%, rgba(255,255,255, 0.1) 17.5%, transparent 30%)',
      },
    });
  }, [updateOptions, sheet.id, imageLoaded]);

  const purchase = () => {
    PurchaseManager.buyCoin(productId);

    ba('fcm-popup', undefined, {
      popup: sheet.id,
      actionType: 'clicked-purchase',
      productId,
      result: sheet.data,
      fbUid: myinfo.fbUid,
      country: myinfo.country,
      device: Device.name,
    });
  };

  return (
    <div className="flex h-full w-full flex-col items-center text-white">
      <div className="absolute top-0 w-full text-center">
        <div className="absolute bottom-0 left-[50%] w-full translate-x-[-50%] text-center">
          <div className="text-[40px] font-bold">{brsc.bonus_single_title}</div>
          <div className="text-[72px] font-bold leading-[100%] text-[#FFC539]">{bonusRatio}%</div>
          {details && (
            <img
              className="z-1 h-[24dvh] w-[24dvh] object-contain"
              alt=""
              src={imgUrl}
              loading="eager"
              style={{
                visibility: imageLoaded ? 'visible' : 'hidden',
                width: yearEnd ? '100%' : '24dvh',
                height: yearEnd ? 'auto' : '24dvh',
                maxHeight: yearEnd ? '40dvh' : 'unset',
              }}
            />
          )}
        </div>
        <SpotLightImage className="mx-auto h-[62dvh] object-contain" />
        {details && (
          <div className="absolute bottom-0 flex w-full translate-y-[100%] flex-col items-center gap-2">
            <div className="typography-title-lg flex gap-2">
              <span className="typography-title-lg">{`${details.coinAmount}${brsc.coins}`}</span>
              <span className="typography-title-lg text-[#FFC539]">+</span>
              <span className="typography-title-lg text-[#FFC539]">{`${details.coinAmount * (bonusRatio / 100)}${brsc.coins}`}</span>
            </div>
            <div className="gr-box typography-body-md rounded-lg px-6 py-2">{details.price}</div>
          </div>
        )}
      </div>
      <div className="flex h-[56px] w-full">
        <Icon onClick={() => close(sheet.id, { skipConfirm: false })} name="icon/ic_close.svg" color="filter-base-ic-white" />
      </div>
      <CoinProgressBar loaded={progressCompleted} />
      <Button onClick={purchase} className="mt-auto w-full" style={{ text: brsc.purchase, background: '#FFC539', fontColor: 'black' }} />
      <CoinPurchaseAnimation ref={coinPurchaseAnimationRef} />
    </div>
  );
};

export default CoinSinglePromotion;

const SpotLightImage = ({ className }: { className?: string }) => {
  return (
    <svg className={className} width="343" height="506" viewBox="0 0 343 506" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M64 0H279L343 470H0L64 0Z" fill="url(#paint0_linear_398_9184)" />
      <ellipse cx="171.5" cy="470" rx="171.5" ry="36" fill="#886C26" />
      <defs>
        <linearGradient id="paint0_linear_398_9184" x1="171.5" y1="0" x2="171.5" y2="470" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FFC539" stop-opacity="0" />
          <stop offset="1" stop-color="#FFC539" stop-opacity="0.15" />
        </linearGradient>
      </defs>
    </svg>
  );
};
